/**
 * CHORUS APP STATE
 * Central state management for the application
 */

import { createStore, createAction } from './store.js';

/**
 * @typedef {Object} Message
 * @property {string} id
 * @property {'user' | 'assistant'} role
 * @property {string} content
 * @property {string} [model] - AI model that generated this (for assistant messages)
 * @property {number} timestamp
 * @property {'pending' | 'streaming' | 'complete' | 'error'} status
 */

/**
 * @typedef {Object} Conversation
 * @property {string} id
 * @property {string} title
 * @property {Message[]} messages
 * @property {number} createdAt
 * @property {number} updatedAt
 */

/**
 * @typedef {Object} AIModel
 * @property {string} id
 * @property {string} name
 * @property {string} provider
 * @property {string} color
 * @property {boolean} enabled
 */

/**
 * Initial application state
 */
const initialState = {
  // UI State
  ui: {
    sidebarOpen: false,
    sidebarCollapsed: false,
    theme: 'dark',
    inputFocused: false,
    isLoading: false,
    error: null,
    activeTab: 'chat', // 'chat' or an agent id like 'main', 'pulse', 'sentinel', 'reviewer-vps'
  },

  // Chat State
  chat: {
    currentConversationId: null,
    inputValue: '',
    isStreaming: false,
  },

  // Conversations
  conversations: [],

  // Agent tab conversations — keyed by agent id, each has messages array
  agentConversations: {},

  // Parallel Mode
  parallelMode: {
    enabled: false,
    activeModels: ['google', 'deepseek'],
  },

  // Available AI Models (for parallel mode chips)
  models: [
    { id: 'google', name: 'Antigravity', provider: 'Google', color: 'var(--accent-primary)', enabled: true },
    { id: 'deepseek', name: 'DeepSeek', provider: 'DeepSeek', color: 'var(--ai-deepseek)', enabled: true },
    { id: 'minimax', name: 'MiniMax', provider: 'MiniMax', color: '#6366f1', enabled: true },
    { id: 'kimi', name: 'Kimi', provider: 'Moonshot', color: '#f59e0b', enabled: true },
    { id: 'zai', name: 'Z.AI', provider: 'Z.AI', color: '#10b981', enabled: true },
    { id: 'openclaw', name: 'OpenClaw', provider: 'OpenClaw', color: '#e11d48', enabled: true },
  ],

  // Artifacts (Canvas panel)
  artifacts: {
    items: {},       // keyed by artifact ID
    activeId: null,  // currently open artifact
    panelOpen: false,
    panelWidth: 480, // default width, persisted
  },

  // Knowledge Base (Document upload + RAG)
  knowledgeBase: {
    documents: [],     // { id, name, size, type, uploadDate, chunkCount, enabled, scope: 'global'|'agent'|'conversation', agentId?: string, conversationId?: string }
    uploading: false,
    uploadProgress: 0,
    panelOpen: false,  // sidebar KB section expanded
  },

  // Per-message attached file IDs (conversation-scoped)
  attachedFiles: [],   // { id, name, docId } — files attached to current input

  // Settings
  settings: {
    temperature: 0.7,
    maxTokens: 4096,
    streamResponses: true,
    smartRouting: true,
    followUpSuggestions: true,
    searchEnabled: false,  // manual toggle per-query
  },

  // Smart Routing suggestion (transient UI state)
  routingSuggestion: null, // { provider, model, label, reason } or null

  // Memory items (loaded from IndexedDB)
  memory: {
    items: [],
    suggestion: null,  // { content: string } — AI-detected "remember this?"
  },

  // Follow-up suggestions (per-message, transient)
  followUps: [], // [{ text: string }]

  // Usage tracking (summary for display)
  usage: {
    currentConv: { inputTokens: 0, outputTokens: 0, cost: 0 },
    total: { inputTokens: 0, outputTokens: 0, cost: 0, messageCount: 0 },
  },

  // Mission Control state
  missionControl: {
    agents: [],          // Agent list from API
    selectedAgent: null, // Currently selected agent ID
    agentHistory: [],    // Session history for selected agent
    cronJobs: [],        // Cron job list
    agentFiles: [],      // File list for selected agent
    fileContent: null,   // Currently viewed file content
    loading: false,
    error: null,
    lastRefresh: null,
  },
};

// Create the store instance
export const store = createStore(initialState);

// ─────────────────────────────────────────
// UI ACTIONS
// ─────────────────────────────────────────

export const toggleSidebar = createAction(store, 'toggleSidebar', (state) => ({
  ui: { sidebarOpen: !state.ui.sidebarOpen },
}));

export const setSidebarOpen = createAction(store, 'setSidebarOpen', (state, open) => ({
  ui: { sidebarOpen: open },
}));

export const toggleSidebarCollapsed = createAction(store, 'toggleSidebarCollapsed', (state) => ({
  ui: { sidebarCollapsed: !state.ui.sidebarCollapsed },
}));

export const setLoading = createAction(store, 'setLoading', (state, isLoading) => ({
  ui: { isLoading },
}));

export const setError = createAction(store, 'setError', (state, error) => ({
  ui: { error },
}));

export const clearError = createAction(store, 'clearError', () => ({
  ui: { error: null },
}));

// ─────────────────────────────────────────
// CHAT ACTIONS
// ─────────────────────────────────────────

export const setInputValue = createAction(store, 'setInputValue', (state, value) => ({
  chat: { inputValue: value },
}));

export const setStreaming = createAction(store, 'setStreaming', (state, isStreaming) => ({
  chat: { isStreaming },
}));

export const setCurrentConversation = createAction(store, 'setCurrentConversation', (state, id) => ({
  chat: { currentConversationId: id },
}));

// ─────────────────────────────────────────
// CONVERSATION ACTIONS
// ─────────────────────────────────────────

export const createConversation = createAction(store, 'createConversation', (state) => {
  const newConversation = {
    id: crypto.randomUUID(),
    title: 'New Chat',
    messages: [],
    createdAt: Date.now(),
    updatedAt: Date.now(),
  };

  return {
    conversations: [newConversation, ...state.conversations],
    chat: { currentConversationId: newConversation.id },
  };
});

export const deleteConversation = createAction(store, 'deleteConversation', (state, id) => {
  const conversations = state.conversations.filter(c => c.id !== id);
  const currentId = state.chat.currentConversationId === id
    ? conversations[0]?.id || null
    : state.chat.currentConversationId;

  return {
    conversations,
    chat: { currentConversationId: currentId },
  };
});

export const addMessage = createAction(store, 'addMessage', (state, { conversationId, message }) => {
  const conversations = state.conversations.map(conv => {
    if (conv.id !== conversationId) return conv;

    const newMessage = {
      id: crypto.randomUUID(),
      timestamp: Date.now(),
      status: 'complete',
      ...message,
    };

    // Update title from first user message
    const title = conv.messages.length === 0 && message.role === 'user'
      ? message.content.slice(0, 50) + (message.content.length > 50 ? '...' : '')
      : conv.title;

    return {
      ...conv,
      title,
      messages: [...conv.messages, newMessage],
      updatedAt: Date.now(),
    };
  });

  return { conversations };
});

export const updateMessage = createAction(store, 'updateMessage', (state, { conversationId, messageId, updates }) => {
  const conversations = state.conversations.map(conv => {
    if (conv.id !== conversationId) return conv;

    const messages = conv.messages.map(msg =>
      msg.id === messageId ? { ...msg, ...updates } : msg
    );

    return { ...conv, messages, updatedAt: Date.now() };
  });

  return { conversations };
});

// ─────────────────────────────────────────
// PARALLEL MODE ACTIONS
// ─────────────────────────────────────────

export const toggleParallelMode = createAction(store, 'toggleParallelMode', (state) => ({
  parallelMode: { enabled: !state.parallelMode.enabled },
}));

export const setParallelMode = createAction(store, 'setParallelMode', (state, enabled) => ({
  parallelMode: { enabled },
}));

export const toggleModel = createAction(store, 'toggleModel', (state, modelId) => {
  const activeModels = state.parallelMode.activeModels.includes(modelId)
    ? state.parallelMode.activeModels.filter(id => id !== modelId)
    : [...state.parallelMode.activeModels, modelId];

  return { parallelMode: { activeModels } };
});

export const setActiveModels = createAction(store, 'setActiveModels', (state, modelIds) => ({
  parallelMode: { activeModels: modelIds },
}));

// ─────────────────────────────────────────
// TAB ACTIONS
// ─────────────────────────────────────────

export const setActiveTab = createAction(store, 'setActiveTab', (state, tabId) => ({
  ui: { activeTab: tabId },
}));

export const addAgentMessage = createAction(store, 'addAgentMessage', (state, { agentId, message }) => {
  const existing = state.agentConversations[agentId] || { messages: [] };
  const newMessage = {
    id: crypto.randomUUID(),
    timestamp: Date.now(),
    status: 'complete',
    ...message,
  };
  return {
    agentConversations: {
      ...state.agentConversations,
      [agentId]: { messages: [...existing.messages, newMessage] },
    },
  };
});

export const setAgentHistory = createAction(store, 'setAgentHistory', (state, { agentId, messages, hasMore, total, offset }) => {
  return {
    agentConversations: {
      ...state.agentConversations,
      [agentId]: {
        messages,
        historyLoaded: true,
        hasMore: hasMore || false,
        total: total || messages.length,
        offset: offset || messages.length,
      },
    },
  };
});

export const prependAgentHistory = createAction(store, 'prependAgentHistory', (state, { agentId, messages, hasMore, offset }) => {
  const existing = state.agentConversations[agentId] || { messages: [] };
  return {
    agentConversations: {
      ...state.agentConversations,
      [agentId]: {
        ...existing,
        messages: [...messages, ...existing.messages],
        hasMore: hasMore || false,
        offset: offset || (existing.offset || 0) + messages.length,
      },
    },
  };
});

export const updateAgentMessage = createAction(store, 'updateAgentMessage', (state, { agentId, messageId, updates }) => {
  const existing = state.agentConversations[agentId];
  if (!existing) return {};
  const messages = existing.messages.map(msg =>
    msg.id === messageId ? { ...msg, ...updates } : msg
  );
  return {
    agentConversations: {
      ...state.agentConversations,
      [agentId]: { messages },
    },
  };
});

// ─────────────────────────────────────────
// SETTINGS ACTIONS
// ─────────────────────────────────────────

export const updateSettings = createAction(store, 'updateSettings', (state, updates) => ({
  settings: { ...state.settings, ...updates },
}));

// ─────────────────────────────────────────
// ROUTING / MEMORY / FOLLOW-UP / USAGE
// ─────────────────────────────────────────

export const setRoutingSuggestion = createAction(store, 'setRoutingSuggestion', (state, suggestion) => ({
  routingSuggestion: suggestion,
}));

export const setMemoryItems = createAction(store, 'setMemoryItems', (state, items) => ({
  memory: { ...state.memory, items },
}));

export const setMemorySuggestion = createAction(store, 'setMemorySuggestion', (state, suggestion) => ({
  memory: { ...state.memory, suggestion },
}));

export const setFollowUps = createAction(store, 'setFollowUps', (state, followUps) => ({
  followUps,
}));

export const setUsageData = createAction(store, 'setUsageData', (state, usage) => ({
  usage,
}));

// ─────────────────────────────────────────
// ARTIFACT ACTIONS
// ─────────────────────────────────────────

export const addArtifact = createAction(store, 'addArtifact', (state, artifact) => ({
  artifacts: {
    items: { ...state.artifacts.items, [artifact.id]: artifact },
  },
}));

export const updateArtifact = createAction(store, 'updateArtifact', (state, { id, updates }) => {
  const existing = state.artifacts.items[id];
  if (!existing) return {};
  return {
    artifacts: {
      items: { ...state.artifacts.items, [id]: { ...existing, ...updates, updatedAt: Date.now() } },
    },
  };
});

export const setActiveArtifact = createAction(store, 'setActiveArtifact', (state, id) => ({
  artifacts: { activeId: id, panelOpen: id !== null },
}));

export const closeArtifactPanel = createAction(store, 'closeArtifactPanel', () => ({
  artifacts: { activeId: null, panelOpen: false },
}));

export const setArtifactPanelWidth = createAction(store, 'setArtifactPanelWidth', (state, width) => ({
  artifacts: { panelWidth: width },
}));

export const removeArtifact = createAction(store, 'removeArtifact', (state, id) => {
  const items = { ...state.artifacts.items };
  delete items[id];
  return {
    artifacts: {
      items,
      activeId: state.artifacts.activeId === id ? null : state.artifacts.activeId,
      panelOpen: state.artifacts.activeId === id ? false : state.artifacts.panelOpen,
    },
  };
});

// ─────────────────────────────────────────
// KNOWLEDGE BASE ACTIONS
// ─────────────────────────────────────────

export const toggleKBPanel = createAction(store, 'toggleKBPanel', (state) => ({
  knowledgeBase: { panelOpen: !state.knowledgeBase.panelOpen },
}));

export const addAttachedFile = createAction(store, 'addAttachedFile', (state, file) => ({
  attachedFiles: [...state.attachedFiles, file],
}));

export const removeAttachedFile = createAction(store, 'removeAttachedFile', (state, fileId) => ({
  attachedFiles: state.attachedFiles.filter(f => f.id !== fileId),
}));

export const clearAttachedFiles = createAction(store, 'clearAttachedFiles', () => ({
  attachedFiles: [],
}));

// ─────────────────────────────────────────
// SELECTORS
// ─────────────────────────────────────────

export const getCurrentConversation = (state) => {
  return state.conversations.find(c => c.id === state.chat.currentConversationId) || null;
};

export const getActiveModels = (state) => {
  return state.models.filter(m => state.parallelMode.activeModels.includes(m.id));
};

export const getEnabledModels = (state) => {
  return state.models.filter(m => m.enabled);
};

// ─────────────────────────────────────────
// MISSION CONTROL ACTIONS
// ─────────────────────────────────────────

export const setMissionAgents = createAction(store, 'setMissionAgents', (state, agents) => ({
  missionControl: { agents, lastRefresh: Date.now() },
}));

export const setMissionSelectedAgent = createAction(store, 'setMissionSelectedAgent', (state, agentId) => ({
  missionControl: { selectedAgent: agentId },
}));

export const setMissionAgentHistory = createAction(store, 'setMissionAgentHistory', (state, history) => ({
  missionControl: { agentHistory: history },
}));

export const setMissionCronJobs = createAction(store, 'setMissionCronJobs', (state, jobs) => ({
  missionControl: { cronJobs: jobs },
}));

export const setMissionAgentFiles = createAction(store, 'setMissionAgentFiles', (state, files) => ({
  missionControl: { agentFiles: files },
}));

export const setMissionFileContent = createAction(store, 'setMissionFileContent', (state, content) => ({
  missionControl: { fileContent: content },
}));

export const setMissionLoading = createAction(store, 'setMissionLoading', (state, loading) => ({
  missionControl: { loading },
}));

export const setMissionError = createAction(store, 'setMissionError', (state, error) => ({
  missionControl: { error },
}));

export default store;
